import React, { useState, useRef, useEffect } from 'react';
import { Search, Check, ChevronDown, X } from 'lucide-react';
import { cn } from '../../utils/cn';

export function Typeahead({
    options = [],
    value,
    onChange,
    onSearch,
    placeholder = 'Search...',
    displayKey = 'label',
    valueKey = 'value',
    className,
    disabled = false,
    loading = false,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (onSearch && searchTerm) {
            onSearch(searchTerm);
        } else {
            setFilteredOptions(options);
        }
    }, [searchTerm, options, onSearch]);

    useEffect(() => {
        setFilteredOptions(options);
    }, [options]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find((opt) => opt[valueKey] === value);

    const handleSelect = (option) => {
        if (option) {
            onChange(option[valueKey]);
            setSearchTerm('');
            setIsOpen(false);
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange(null);
        setSearchTerm('');
        setIsOpen(false);
    };

    const displayValue = selectedOption ? selectedOption[displayKey] : '';

    return (
        <div ref={containerRef} className={cn('relative', className)}>
            <div
                className={cn(
                    'flex items-center gap-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm',
                    'focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-200',
                    disabled && 'opacity-50 cursor-not-allowed bg-slate-50'
                )}
                onClick={() => !disabled && !isOpen && setIsOpen(true)}
            >
                <Search className="h-4 w-4 text-slate-400 shrink-0" />
                <input
                    ref={inputRef}
                    type="text"
                    value={isOpen ? searchTerm : displayValue}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="flex-1 outline-none bg-transparent"
                />
                {value && !disabled && (
                    <button type="button" onClick={handleClear} className="text-slate-400 hover:text-slate-600">
                        <X className="h-4 w-4" />
                    </button>
                )}
                <ChevronDown
                    className={cn(
                        'h-4 w-4 text-slate-400 shrink-0 transition-transform',
                        isOpen && 'transform rotate-180'
                    )}
                />
            </div>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {loading ? (
                        <div className="px-4 py-3 text-sm text-slate-500 text-center">Loading...</div>
                    ) : filteredOptions.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-slate-500 text-center">No users found</div>
                    ) : (
                        filteredOptions.map((option) => {
                            const isSelected = value === option[valueKey];
                            return (
                                <button
                                    key={option[valueKey]}
                                    type="button"
                                    onClick={() => handleSelect(option)}
                                    className={cn(
                                        'w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2',
                                        isSelected && 'bg-primary-50 text-primary-700'
                                    )}
                                >
                                    {isSelected && <Check className="h-4 w-4 text-primary-600 shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium break-words">{option[displayKey]}</div>
                                        {option.subtitle && (
                                            <div className="text-xs text-slate-500 break-words">{option.subtitle}</div>
                                        )}
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}
